// Copyright (c) StrangeBee 2023

import { useState, useEffect } from 'react';
import { AttachmentDataType } from '@Pages/Cases/Details/Attachments/List/AttachmentTasksList';
import { AttachmentCaseType, AttachmentType } from 'thehive-sdk';
import { getFileDatastoreUrl } from '@Helpers/';
import { openMessage, useLocalMessage } from '@Helpers/';
import { Buffer } from 'buffer';
import { THPreviewType } from './THPreview';

export const supportedAttachmentTypes: Record<string, string[]> = {
    text: [
        'text/html',
        'text/plain',
        'application/json',
        'application/ld+json',
        'application/rtf',
        'application/xml',
        'text/xml',
        'text/css',
        'text/html',
        'text/javascript',
        'application/xhtml+xml',
        'text/rtf',
        'application/octet-stream',
        'text/x-python',
        'text/x-perl',
        'text/x-ruby',
        'text/x-shellscript',
        'text/x-php',
        'text/x-java',
        'text/x-c',
        'text/x-c++',
        'text/x-csharp',
        'text/x-objective-c',
        'text/x-swift',
        'text/x-go',
        'text/x-rust',
        'text/x-haskell',
        'text/x-lua',
        'text/x-scala',
        'text/x-kotlin',
        'text/x-vbnet',
        'text/x-pascal',
        'text/x-tcl',
        'text/x-matlab',
        'text/x-groovy',
        'text/x-dart',
        'text/x-clojure',
        'text/x-elixir',
        'text/x-crystal',
        'text/x-erlang',
        'text/x-lisp',
        'text/x-ocaml',
        'text/x-prolog',
        'text/x-scheme',
        'text/x-yaml',
        'text/x-plaintext',
        'text/x-sh',
        'text/x-python-script',
        'text/x-perl-script',
        'text/x-ruby-script',
        'text/x-shellscript',
        'text/x-php-script',
        'text/x-javascript-script',
        'text/x-lua-script',
        'text/x-tcl-script',
        'text/x-vbscript',
        'text/x-applescript',
        'text/x-powershell-script',
    ],
    csv: ['text/csv'],
    pdf: ['application/pdf'],
    image: [
        'image/jpeg',
        'image/png',
        'image/gif',
        'image/bmp',
        'image/webp',
        'image/svg+xml',
        'image/x-icon',
        'image/vnd.microsoft.icon',
    ],
};

export type GenericAttachmentType = AttachmentDataType | AttachmentCaseType | AttachmentType;

export const usePreview = () => {
    const [isError, setIsError] = useState(false);
    const [content, setContent] = useState<string | null>(null);
    const [type, setType] = useState<THPreviewType | null>(null);

    const i = useLocalMessage();

    useEffect(() => {
        if (isError) {
            openMessage('error', i('attachment.action.preview.error'));
        }
    }, [isError, i]);

    const fetchContent = (url: string, type: string, extension: string) => {
        fetch(url)
            .then((response) => {
                if (response.ok) {
                    return response.arrayBuffer();
                }

                throw new Error('error during fetching Image');
            })
            .then((text) => {
                setType(type === 'image' ? 'image' : 'pdf');
                setContent(`data:${type}/${extension};base64,` + Buffer.from(text).toString('base64'));
            })
            .catch(() => setIsError(true))
            .finally(() => setIsError(false));
    };

    const fetchText = (url: string, extension: string) => {
        fetch(url)
            .then((response) => {
                if (response.ok) {
                    return response.text();
                }

                throw new Error('error during fetching Text');
            })
            .then((text) => {
                setType(extension === 'csv' ? extension : 'text');
                setContent(text);
            })
            .catch(() => setIsError(true))
            .finally(() => setIsError(false));
    };

    const togglePreview = (attachment: GenericAttachmentType) => {
        if (attachment) {
            const { contentType, id } = attachment;

            const fileLocation = window.location.origin + getFileDatastoreUrl(id);
            const [type, extension] = contentType.split('/');

            switch (type) {
                case 'image':
                    fetchContent(fileLocation, type, extension);
                    break;
                case 'text':
                case 'application':
                    if (extension === 'pdf') {
                        fetchContent(fileLocation, type, extension);
                    } else {
                        fetchText(fileLocation, extension);
                    }
                    break;
            }
        } else {
            setContent(null);
        }
    };

    const allowPreview = (contentType: string) =>
        !!Object.keys(supportedAttachmentTypes).find((key) => supportedAttachmentTypes[key].includes(contentType));

    return { allowPreview, togglePreview, content, type };
};
